import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CalendarClock, X, ChevronDown, ChevronUp } from "lucide-react";

const dismissKey = (s) => `bf.rollover.dismissed.${s}`;

/** Reads the rollover gap for the session in play. Returns null until it knows. */
export const useRolloverStatus = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/booklists/rollover-status").then((r) => setData(r.data)).catch(() => {});
  }, []);
  return data;
};

/**
 * The nudge at session start: last session's schools whose booklists have not been
 * carried over yet. Dismissable, and the dismissal is remembered per session.
 */
export const RolloverReminder = ({ onRollover }) => {
  const data = useRolloverStatus();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const session = data?.current_session || "";
  const count = data?.counts?.schools_pending || 0;
  if (!session || !count || hidden || localStorage.getItem(dismissKey(session))) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey(session), "1");
    setHidden(true);
  };

  return (
    <div data-testid="rollover-reminder"
      className="mb-5 border border-[#F0C9A0] bg-[#FFF8F0] rounded-[3px]">
      <div className="p-4 flex items-start gap-3">
        <CalendarClock size={18} className="text-[#B4530A] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-[#B4530A]">
            {count} school{count > 1 ? "s" : ""} not rolled over into {session}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {data.counts.classes_pending} class booklist{data.counts.classes_pending > 1 ? "s" : ""} from{" "}
            {data.previous_session} have no counterpart in {session} yet.
          </p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button data-testid="rollover-reminder-run-btn" className="btn-primary h-8 text-xs"
              onClick={() => onRollover?.(data.previous_session, session)}>
              Roll over now
            </Button>
            <Button data-testid="rollover-reminder-toggle-btn" variant="outline"
              className="rounded-[2px] h-8 text-xs" onClick={() => setOpen(!open)}>
              {open ? <ChevronUp size={14} className="mr-1" /> : <ChevronDown size={14} className="mr-1" />}
              {open ? "Hide schools" : "See schools"}
            </Button>
          </div>
        </div>
        <button data-testid="rollover-reminder-dismiss-btn" onClick={dismiss}
          title="Remind me no more this session"
          className="text-muted-foreground hover:text-foreground shrink-0">
          <X size={16} />
        </button>
      </div>
      {open && (
        <div className="border-t border-[#F0C9A0] max-h-64 overflow-auto"
          data-testid="rollover-reminder-list">
          {data.pending.map((p) => (
            <div key={p.school_id}
              className="px-4 py-2.5 border-b border-[#F5E2CE] last:border-0 flex items-baseline gap-3">
              <span className="text-sm font-semibold flex-1 min-w-0 truncate">{p.school_name}</span>
              <span className="text-xs text-muted-foreground truncate max-w-[45%]">
                {p.pending_classes.join(", ")}
              </span>
              <span className="mono text-xs font-bold tabular-nums">{p.pending_count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
